// FI-2180: session renewal helpers. the api (FI-2178) hands out a fresh jwt
// on POST bookings/auth/refresh while the current one is still valid, so a
// signed-in browser can stay signed in across releases instead of being
// bounced to the login page when the token runs out.
//
// pure helpers, no vue imports, so every decision here is unit-testable on
// its own. the callers are App.vue (the timed renewal), networking/index.js
// (the 403 classification and the retry-after-refresh) and login.vue (the
// "already signed in, forward" gate).

// localStorage: when the last renewal attempt was made (ms). written BEFORE
// the call so parallel tabs do not stampede the endpoint.
export const RENEW_ATTEMPT_KEY = 'fi_session_renew_attempt_at'
// sessionStorage: set by an explicit sign-out right before the push to
// /login, so login.vue clears the session instead of forwarding.
export const SIGNOUT_KEY = 'fi_signout'
// sessionStorage: why the session ended ('expired' | 'invalid'), set by the
// bounce in networking/index.js or App.vue so login.vue can say so.
export const SESSION_END_REASON_KEY = 'fi_session_end_reason'

const DAY_MS = 24 * 60 * 60 * 1000
export const RENEW_WHEN_REMAINING_MS = 4 * DAY_MS
export const RENEW_MIN_GAP_MS = 10 * 60 * 1000
export const DEFAULT_SKEW_MS = 60000

// jwt segments are base64url (RFC 7515); atob wants plain base64.
function decodeSegment(segment) {
  const b64 = String(segment).replace(/-/g, '+').replace(/_/g, '/')
  const padded = b64 + '==='.slice(0, (4 - (b64.length % 4)) % 4)
  return atob(padded)
}

// the token's exp claim in SECONDS, or null for anything that is not a
// well-formed jwt carrying a numeric exp. mirrors helper.js h_get_jwt_data
// (split on '.', decode segment 2, JSON.parse) but never throws.
export function tokenExp(jwt) {
  if (!jwt || typeof jwt !== 'string') return null
  try {
    const parts = jwt.split('.')
    if (parts.length !== 3) return null
    const payload = JSON.parse(decodeSegment(parts[1]))
    const exp = payload && payload.exp
    return typeof exp === 'number' && isFinite(exp) ? exp : null
  } catch (_) {
    return null
  }
}

// true once the token is within skewMs of its exp (or has no readable exp:
// a token we cannot read is not one we can keep using).
export function isTokenExpired(jwt, nowMs, skewMs = DEFAULT_SKEW_MS) {
  const exp = tokenExp(jwt)
  if (exp == null) return true
  return exp * 1000 - skewMs <= nowMs
}

// renew only inside the last stretch of the token's life, and not more often
// than minGapMs between attempts (successful or not). exp is in seconds.
export function shouldRenewToken({
  exp, nowMs, lastAttemptAt,
  renewWhenRemainingMs = RENEW_WHEN_REMAINING_MS,
  minGapMs = RENEW_MIN_GAP_MS,
} = {}) {
  if (exp == null || typeof exp !== 'number' || !isFinite(exp)) return false
  const remaining = exp * 1000 - nowMs
  if (!(remaining < renewWhenRemainingMs)) return false
  const last = Number(lastAttemptAt) || 0
  if (last && nowMs - last < minGapMs) return false
  return true
}

// FI-2273: the api's answer for a valid session it will not extend (FI-2269:
// backdoor and tbn-verified logins, and tokens minted before FI-2178)
export const NOT_RENEWABLE_ERROR_CODE = 9

export function isNotRenewableReply({ httpStatus, errorCode } = {}) {
  return Number(httpStatus) === 409 && Number(errorCode) === NOT_RENEWABLE_ERROR_CODE
}

// FI-2273: what a failed refresh means for the session, as 'expired' |
// 'invalid' (end it) or null (keep it). a jwt past its own exp is over
// whatever the reply was (a 404 while the api lacks the route, a 5xx, no
// network at all); a 401 with error_code 3 says the token itself is no good
// (password changed, user removed, passkey revoked). any other refusal of a
// token still valid here, 409/9 or a 401/4 from an api without FI-2269, keeps
// the session until its own exp.
export function sessionEndAfterFailedRenewal({ httpStatus, errorCode, jwt, nowMs } = {}) {
  if (isTokenExpired(jwt, nowMs == null ? Date.now() : nowMs)) return 'expired'
  if (Number(httpStatus) === 401 && Number(errorCode) === 3) return 'invalid'
  return null
}

// FI-2273: what App.vue maybeRenewSession does before asking the api.
// 'signed-out': the shared jwt is gone while this tab is signed in (another
// tab signed out). 'end-expired': the jwt is past its own exp; the quiet
// polls never bounce, so this is what ends an idle tab. an expired token is
// never offered for renewal, even before the tab counts as signed in (App.vue
// created() runs this first). 'renew' or 'skip' otherwise.
export function renewalPrecheck({ jwt, authenticated, lastAttemptAt, nowMs } = {}) {
  if (!jwt) return authenticated ? 'signed-out' : 'skip'
  if (isTokenExpired(jwt, nowMs)) return authenticated ? 'end-expired' : 'skip'
  return shouldRenewToken({ exp: tokenExp(jwt), nowMs, lastAttemptAt }) ? 'renew' : 'skip'
}

// FI-2273: what to do with a failed renewal of offeredJwt. 'stale': a newer
// sign-in replaced the token while the call was out, so nothing here may
// touch the session. 'park': the api will never extend this token (409/9).
// 'expired' | 'invalid': end the session. 'keep': try again later.
export function renewalFailureAction({ offeredJwt, storedJwt, httpStatus, errorCode, nowMs } = {}) {
  if (storedJwt !== offeredJwt) return 'stale'
  if (isNotRenewableReply({ httpStatus, errorCode })) return 'park'
  return sessionEndAfterFailedRenewal({ httpStatus, errorCode, jwt: offeredJwt, nowMs }) || 'keep'
}

function tenantOptionValue(opt) {
  return opt && typeof opt === 'object' ? opt.value : opt
}

// write the renewed session the way login.vue's handle_login_response does
// (user, jwt, authenticated, expiration_token_time) and hand back the user
// the app should carry: the api's data.tenant is the DEFAULT landing tenant,
// so a picker selection kept in storage.selected_tenant is re-applied when
// the renewed user may still see it (App.vue created() does the same at
// boot). a selection the user has since lost is dropped from storage so the
// next boot does not resurrect it. fi_theme, view_as and
// fi_nav_favorites_bar are left alone: they are per-browser choices, not
// session state.
export function applyRenewedSession(storage, { token, user } = {}) {
  // a reply without both halves is treated as a transient failure by the
  // callers; never let 'undefined' land in storage where a later boot would
  // JSON.parse it
  if (!token || !user || typeof user !== 'object') throw new Error('refresh response missing token or user')
  storage.setItem('user', JSON.stringify(user))
  storage.setItem('jwt', token)
  storage.setItem('authenticated', true)
  const exp = tokenExp(token)
  if (exp != null) storage.setItem('expiration_token_time', exp)

  const merged = { ...(user || {}) }
  const selected = storage.getItem('selected_tenant')
  if (selected != null) {
    const options = (user && Array.isArray(user.tenant_options)) ? user.tenant_options : []
    const allowed = options.some(opt => tenantOptionValue(opt) === selected)
    if (allowed) merged.tenant = selected
    else storage.removeItem('selected_tenant')
  }
  return merged
}

// where a signed-in user lands when nothing asked for a specific page.
// reproduces login.vue handle_login_response's landing rules exactly.
// the api said this token is over ('expired' | 'invalid'). record why for the
// login page and drop the dead token, so the login page's forward gate cannot
// carry it back into the app (a live jwt in storage afterwards can only mean a
// newer sign-in, which the gate then honours over the leftover reason).
export function endSessionLocally(storage, sessionStore, kind) {
  try { sessionStore.setItem(SESSION_END_REASON_KEY, kind) } catch (_) { /* private mode */ }
  try {
    storage.removeItem('jwt')
    storage.removeItem('expiration_token_time')
    storage.removeItem('authenticated')
  } catch (_) { /* private mode */ }
}

// FI-2268: an explicit visit to the login page (#/login typed, or a bookmark
// of the login page itself) is how a shared workstation hands over, so it
// signs a live session out. the bare origin reaches login through the
// router's '/' redirect (redirectedFrom is set) and #/login/<page> is
// login_redirect; both forward into a live session
export function isExplicitLoginVisit(route) {
  return !!route && route.name === 'login' && !route.redirectedFrom
}

export function homeRouteFor(user) {
  const u = user || {}
  if (u.role === 'driver' && u.tenant === 'dattco-413f464d') {
    return { name: 'driver_bid_board_tbn' }
  }
  if (u.home_page) return u.home_page
  if (u.role === 'booking-customer') return '/booking-customer'
  return 'dashboard'
}

// the login route to bounce to after the session ends, carrying the current
// hash path as :nextUrl so login.vue can bring the user back. hashPath is
// window.location.hash (with or without the leading '#/'). a login path is
// never carried: it would loop the user straight back to the login page.
// null when the page is already on a login route: several requests can fail
// in the same tick, and a second bounce must not rewrite the first one's
// nextUrl to a bare /login
export function loginHrefFor(hashPath) {
  const path = String(hashPath || '').replace(/^#?\/?/, '')
  if (path === 'login' || path.startsWith('login/') || path.startsWith('login?')) return null
  if (!path) return './#/login'
  return './#/login/' + encodeURIComponent(path)
}

// what an api rejection means for the session.
//   'expired'   the token ran out (error_code 4, or a 403 with a jwt that is
//               expired by its own exp): sign in again.
//   'invalid'   the token is not one the api knows (error_code 3: password
//               changed, passkey removed, user gone), or there is no token
//               at all behind a 401/403: sign in again.
//   'forbidden' a 403 with a jwt that is still valid locally: a permission
//               denial, or a tenant route that has not learnt to say 401 yet.
//               worth one refresh-and-retry before giving up.
//   'other'     not an auth problem.
export function classifyAuthFailure({ httpStatus, bodyStatusCode, errorCode, jwt, nowMs } = {}) {
  const code = Number(errorCode)
  if (code === 4) return 'expired'
  if (code === 3) return 'invalid'
  const status = Number(httpStatus)
  const bodyStatus = Number(bodyStatusCode)
  const authStatus = status === 401 || status === 403 || bodyStatus === 401 || bodyStatus === 403
  if (!jwt) return authStatus ? 'invalid' : 'other'
  if (status === 403 || bodyStatus === 403) {
    return isTokenExpired(jwt, nowMs == null ? Date.now() : nowMs) ? 'expired' : 'forbidden'
  }
  return 'other'
}
